import React, { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { Plus, Trash2, X, Upload, Eye, EyeOff, FileText, ChevronDown, ChevronUp, ArrowUp, ArrowDown, Youtube, Image } from 'lucide-react';
import { useToast } from '../../../hooks/use-toast';
import ImagePickerModal from './ImagePickerModal';

const SECTION_TYPES = [
  { value: 'hero', label: 'Hero Banner', description: 'Large header with title and subtitle' },
  { value: 'content', label: 'Content Block', description: 'Text content section' },
  { value: 'cards', label: 'Cards Grid', description: 'Grid of cards with icons' },
  { value: 'features', label: 'Features List', description: 'List of features with icons' },
  { value: 'gallery', label: 'Image/Video Gallery', description: 'Grid of images and YouTube videos' },
  { value: 'cta', label: 'Call to Action', description: 'CTA with button' },
  { value: 'banner', label: 'Banner', description: 'Simple colored banner' },
  { value: 'video', label: 'Video Section', description: 'Featured YouTube video with title' }
];

const PagesAdmin = () => {
  const { toast } = useToast();
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [showAddSection, setShowAddSection] = useState(false);
  const [newSection, setNewSection] = useState({ type: 'content', title: '', position: 0 });
  
  // Image picker state
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [imagePickerTarget, setImagePickerTarget] = useState(null); // { sectionId, itemIndex, field }

  const fetchPages = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/page-content/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // 'home' is managed by the dedicated Home Page Editor to avoid clobbering its typed sections
        setPages(data.filter((p) => p.page_id !== 'home'));
      }
    } catch (error) {
      console.error('Error fetching pages:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPageContent = async (pageId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/page-content/admin/${pageId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Ensure sections are sorted by order
        if (data.sections) {
          data.sections.sort((a, b) => (a.order || 0) - (b.order || 0));
        }
        setPageContent(data);
        const expanded = {};
        data.sections?.forEach(s => { expanded[s.id] = false; }); // Collapse by default
        setExpandedSections(expanded);
      }
    } catch (error) {
      console.error('Error fetching page content:', error);
    }
  };

  useEffect(() => {
    fetchPages();
  }, []);

  useEffect(() => {
    if (selectedPage) {
      fetchPageContent(selectedPage);
    }
  }, [selectedPage]);

  const handleSave = async () => {
    if (!pageContent) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/page-content/${pageContent.page_id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          page_title: pageContent.page_title,
          meta_description: pageContent.meta_description,
          sections: pageContent.sections,
          is_published: pageContent.is_published
        })
      });
      
      if (res.ok) {
        alert('Page saved successfully!');
        fetchPages();
      } else {
        alert('Error saving page');
      }
    } catch (error) {
      console.error('Error saving:', error);
      alert('Error saving page');
    } finally {
      setSaving(false);
    }
  };

  const updateSection = (sectionId, field, value) => {
    setPageContent(prev => ({
      ...prev,
      sections: prev.sections.map(s => 
        s.id === sectionId ? { ...s, [field]: value } : s
      )
    }));
  };

  const updateSectionOrder = (sectionId, newOrder) => {
    setPageContent(prev => {
      const sections = [...prev.sections];
      const sectionIndex = sections.findIndex(s => s.id === sectionId);
      if (sectionIndex === -1) return prev;
      
      // Update the order
      sections[sectionIndex] = { ...sections[sectionIndex], order: newOrder };
      
      // Sort by order
      sections.sort((a, b) => (a.order || 0) - (b.order || 0));
      
      return { ...prev, sections };
    });
  };

  const moveSection = (sectionId, direction) => {
    setPageContent(prev => {
      const sections = [...prev.sections];
      const currentIndex = sections.findIndex(s => s.id === sectionId);
      
      if (currentIndex === -1) return prev;
      if (direction === 'up' && currentIndex === 0) return prev;
      if (direction === 'down' && currentIndex === sections.length - 1) return prev;
      
      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      
      // Swap positions
      [sections[currentIndex], sections[newIndex]] = [sections[newIndex], sections[currentIndex]];
      
      // Update order numbers
      sections.forEach((s, i) => { s.order = i; });
      
      return { ...prev, sections };
    });
  };

  const addNewSection = () => {
    const id = `section-${Date.now()}`;
    const newSectionData = {
      id,
      type: newSection.type,
      title: newSection.title || SECTION_TYPES.find(t => t.value === newSection.type)?.label || 'New Section',
      subtitle: '',
      content: '',
      items: [],
      order: newSection.position,
      is_active: true
    };

    setPageContent(prev => {
      const sections = [...(prev.sections || [])];
      
      // Insert at position
      sections.splice(newSection.position, 0, newSectionData);
      
      // Reorder all sections
      sections.forEach((s, i) => { s.order = i; });
      
      return { ...prev, sections };
    });

    setExpandedSections(prev => ({ ...prev, [id]: true }));
    setShowAddSection(false);
    setNewSection({ type: 'content', title: '', position: 0 });
  };

  const deleteSection = (sectionId) => {
    if (!window.confirm('Are you sure you want to delete this section?')) return;
    
    setPageContent(prev => {
      const sections = prev.sections.filter(s => s.id !== sectionId);
      // Reorder remaining sections
      sections.forEach((s, i) => { s.order = i; });
      return { ...prev, sections };
    });
  };

  const updateSectionItem = (sectionId, itemIndex, field, value) => {
    setPageContent(prev => ({
      ...prev,
      sections: prev.sections.map(s => {
        if (s.id !== sectionId) return s;
        const newItems = [...(s.items || [])];
        newItems[itemIndex] = { ...newItems[itemIndex], [field]: value };
        return { ...s, items: newItems };
      })
    }));
  };

  const addSectionItem = (sectionId, type, mediaType = 'image') => {
    let newItem;
    if (type === 'gallery') {
      newItem = mediaType === 'video' 
        ? { type: 'video', youtube_url: '', caption: '' }
        : { type: 'image', url: '', caption: '' };
    } else {
      newItem = { icon: '✨', title: '', description: '' };
    }
    
    setPageContent(prev => ({
      ...prev,
      sections: prev.sections.map(s => {
        if (s.id !== sectionId) return s;
        return { ...s, items: [...(s.items || []), newItem] };
      })
    }));
  };

  const removeSectionItem = (sectionId, itemIndex) => {
    setPageContent(prev => ({
      ...prev,
      sections: prev.sections.map(s => {
        if (s.id !== sectionId) return s;
        const newItems = [...(s.items || [])];
        newItems.splice(itemIndex, 1);
        return { ...s, items: newItems };
      })
    }));
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const handleImageUpload = async (sectionId, itemIndex, e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Videos allowed up to 50MB, images up to 5MB
    const isVideoFile = (file.type || '').startsWith('video/') || /\.(mp4|mov|webm)$/i.test(file.name || '');
    const maxSize = isVideoFile ? 50 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast({
        title: 'File too large',
        description: isVideoFile ? 'Maximum video size is 50MB.' : 'Maximum image size is 5MB. Please compress or resize the image.',
        variant: 'destructive'
      });
      return;
    }

    // Upload to server
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });

      if (response.ok) {
        const result = await response.json();
        if (itemIndex !== undefined) {
          const isVid = (result.type || file.type || '').startsWith('video') || /\.(mp4|mov|webm)$/i.test(result.url || '');
          updateSectionItem(sectionId, itemIndex, 'url', result.url);
          updateSectionItem(sectionId, itemIndex, 'is_video', isVid);
          updateSectionItem(sectionId, itemIndex, 'type', 'image');
        }
        toast({
          title: 'Media uploaded',
          description: 'Saved successfully'
        });
      } else {
        const error = await response.json();
        toast({
          title: 'Upload failed',
          description: error.detail || 'Failed to upload image',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload failed',
        description: 'Network error uploading image',
        variant: 'destructive'
      });
    }
  };

  // Helper to extract YouTube video ID from URL
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? match[1] : null;
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-2xl sm:text-3xl font-black">Page Content</h2>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">Manage content for website pages</p>
        </div>
      </div>

      {/* Page Selector */}
      <div className="bg-white rounded-xl border-2 border-gray-200 p-4 mb-6">
        <label className="block text-sm font-bold mb-2">Select Page to Edit</label>
        <div className="flex flex-wrap gap-2">
          {pages.length === 0 ? (
            <p className="text-gray-500 text-sm">Loading pages...</p>
          ) : (
            pages.map(page => (
              <button
                key={page.page_id}
                onClick={() => setSelectedPage(page.page_id)}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2 ${
                  selectedPage === page.page_id
                    ? 'bg-purple-500 text-white'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                <FileText className="w-4 h-4" />
                {page.page_title}
              </button>
            ))
          )}
        </div>
      </div>

      {/* Page Editor */}
      {pageContent && (
        <div className="space-y-6">
          {/* Page Settings */}
          <div className="bg-white rounded-xl border-2 border-purple-200 p-4 md:p-6">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-500" />
              Page Settings
            </h3>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold mb-2">Page Title</label>
                <input
                  type="text"
                  value={pageContent.page_title || ''}
                  onChange={(e) => setPageContent({ ...pageContent, page_title: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border-2 focus:border-purple-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-bold mb-2">Meta Description (SEO)</label>
                <input
                  type="text"
                  value={pageContent.meta_description || ''}
                  onChange={(e) => setPageContent({ ...pageContent, meta_description: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border-2 focus:border-purple-500 focus:outline-none"
                />
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2">
              <input
                type="checkbox"
                id="is_published"
                checked={pageContent.is_published !== false}
                onChange={(e) => setPageContent({ ...pageContent, is_published: e.target.checked })}
                className="w-5 h-5 accent-purple-500"
              />
              <label htmlFor="is_published" className="font-bold cursor-pointer">
                Published (visible on website)
              </label>
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <h3 className="text-lg font-bold">Page Sections ({pageContent.sections?.length || 0})</h3>
              <Button
                onClick={() => {
                  setNewSection({ type: 'content', title: '', position: pageContent.sections?.length || 0 });
                  setShowAddSection(true);
                }}
                className="bg-green-500 hover:bg-green-600 gap-2 w-full sm:w-auto"
              >
                <Plus className="w-4 h-4" />
                Add New Section
              </Button>
            </div>

            {/* Add Section Modal */}
            {showAddSection && (
              <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4 space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-bold text-green-800">Add New Section</h4>
                  <button onClick={() => setShowAddSection(false)} className="text-gray-500 hover:text-gray-700">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-bold mb-2">Section Type</label>
                    <select
                      value={newSection.type}
                      onChange={(e) => setNewSection({ ...newSection, type: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border-2 focus:border-green-500 focus:outline-none"
                    >
                      {SECTION_TYPES.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {SECTION_TYPES.find(t => t.value === newSection.type)?.description}
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-bold mb-2">Section Title</label>
                    <input
                      type="text"
                      value={newSection.title}
                      onChange={(e) => setNewSection({ ...newSection, title: e.target.value })}
                      placeholder="e.g., Our Services"
                      className="w-full px-3 py-2 rounded-lg border-2 focus:border-green-500 focus:outline-none"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-bold mb-2">Position (Order)</label>
                    <select
                      value={newSection.position}
                      onChange={(e) => setNewSection({ ...newSection, position: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 rounded-lg border-2 focus:border-green-500 focus:outline-none"
                    >
                      {[...Array((pageContent.sections?.length || 0) + 1)].map((_, i) => (
                        <option key={i} value={i}>
                          {i === 0 ? '1 (First)' : i === (pageContent.sections?.length || 0) ? `${i + 1} (Last)` : i + 1}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowAddSection(false)}>
                    Cancel
                  </Button>
                  <Button onClick={addNewSection} className="bg-green-500 hover:bg-green-600">
                    Add Section
                  </Button>
                </div>
              </div>
            )}
            
            {pageContent.sections?.map((section, sectionIndex) => (
              <div 
                key={section.id} 
                className={`bg-white rounded-xl border-2 overflow-hidden ${
                  section.is_active ? 'border-gray-200' : 'border-gray-100 opacity-60'
                }`}
              >
                {/* Section Header */}
                <div 
                  className="p-3 md:p-4 bg-gray-50 flex items-center justify-between cursor-pointer hover:bg-gray-100"
                  onClick={() => toggleSection(section.id)}
                >
                  <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); moveSection(section.id, 'up'); }}
                        disabled={sectionIndex === 0}
                        className={`p-1 rounded ${sectionIndex === 0 ? 'text-gray-300' : 'text-gray-500 hover:bg-gray-200'}`}
                        title="Move up"
                      >
                        <ArrowUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); moveSection(section.id, 'down'); }}
                        disabled={sectionIndex === pageContent.sections.length - 1}
                        className={`p-1 rounded ${sectionIndex === pageContent.sections.length - 1 ? 'text-gray-300' : 'text-gray-500 hover:bg-gray-200'}`}
                        title="Move down"
                      >
                        <ArrowDown className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs font-bold flex-shrink-0">
                        #{sectionIndex + 1}
                      </span>
                      <span className="font-bold truncate">{section.title || section.type}</span>
                      <span className="text-xs text-gray-500 px-2 py-0.5 bg-gray-200 rounded hidden sm:inline">
                        {section.type}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        updateSection(section.id, 'is_active', !section.is_active);
                      }}
                      className={`p-2 rounded-lg ${section.is_active ? 'text-green-600' : 'text-gray-400'}`}
                      title={section.is_active ? 'Visible' : 'Hidden'}
                    >
                      {section.is_active ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteSection(section.id); }}
                      className="p-2 rounded-lg text-red-500 hover:bg-red-50"
                      title="Delete section"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                    {expandedSections[section.id] ? (
                      <ChevronUp className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    )}
                  </div>
                </div>

                {/* Section Content */}
                {expandedSections[section.id] && (
                  <div className="p-4 border-t space-y-4">
                    {/* Position/Order Input */}
                    <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                      <label className="text-sm font-bold">Position:</label>
                      <select
                        value={sectionIndex}
                        onChange={(e) => {
                          const newPos = parseInt(e.target.value);
                          if (newPos !== sectionIndex) {
                            // Move section to new position
                            setPageContent(prev => {
                              const sections = [...prev.sections];
                              const [removed] = sections.splice(sectionIndex, 1);
                              sections.splice(newPos, 0, removed);
                              sections.forEach((s, i) => { s.order = i; });
                              return { ...prev, sections };
                            });
                          }
                        }}
                        className="px-3 py-1 rounded border-2 focus:border-purple-500 focus:outline-none"
                      >
                        {pageContent.sections.map((_, i) => (
                          <option key={i} value={i}>
                            {i + 1}{i === 0 ? ' (First)' : i === pageContent.sections.length - 1 ? ' (Last)' : ''}
                          </option>
                        ))}
                      </select>
                      <span className="text-xs text-gray-500">Change to reorder this section</span>
                    </div>

                    {/* Title & Subtitle (for most section types) */}
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-bold mb-2">Section Title</label>
                        <input
                          type="text"
                          value={section.title || ''}
                          onChange={(e) => updateSection(section.id, 'title', e.target.value)}
                          className="w-full px-4 py-2 rounded-lg border-2 focus:border-purple-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-bold mb-2">Subtitle</label>
                        <input
                          type="text"
                          value={section.subtitle || ''}
                          onChange={(e) => updateSection(section.id, 'subtitle', e.target.value)}
                          className="w-full px-4 py-2 rounded-lg border-2 focus:border-purple-500 focus:outline-none"
                        />
                      </div>
                    </div>

                    {/* Content field for content/banner types */}
                    {['content', 'banner'].includes(section.type) && (
                      <div>
                        <label className="block text-sm font-bold mb-2">Content</label>
                        <textarea
                          value={section.content || ''}
                          onChange={(e) => updateSection(section.id, 'content', e.target.value)}
                          className="w-full px-4 py-2 rounded-lg border-2 focus:border-purple-500 focus:outline-none"
                          rows="3"
                          placeholder="Enter content text..."
                        />
                      </div>
                    )}

                    {/* Items (for cards, features) */}
                    {['cards', 'features'].includes(section.type) && (
                      <div>
                        <div className="flex justify-between items-center mb-3">
                          <label className="block text-sm font-bold">Items</label>
                          <Button
                            onClick={() => addSectionItem(section.id, section.type)}
                            size="sm"
                            className="bg-purple-500 hover:bg-purple-600 gap-1"
                          >
                            <Plus className="w-4 h-4" />
                            Add Item
                          </Button>
                        </div>
                        <div className="space-y-3">
                          {section.items?.map((item, itemIndex) => (
                            <div key={itemIndex} className="p-3 bg-gray-50 rounded-lg">
                              <div className="flex gap-3 mb-2">
                                <div className="w-16">
                                  <label className="block text-xs font-bold mb-1">Icon</label>
                                  <input
                                    type="text"
                                    value={item.icon || ''}
                                    onChange={(e) => updateSectionItem(section.id, itemIndex, 'icon', e.target.value)}
                                    className="w-full px-2 py-1 rounded border text-center text-xl"
                                    placeholder="🎂"
                                  />
                                </div>
                                <div className="flex-1">
                                  <label className="block text-xs font-bold mb-1">Title</label>
                                  <input
                                    type="text"
                                    value={item.title || ''}
                                    onChange={(e) => updateSectionItem(section.id, itemIndex, 'title', e.target.value)}
                                    className="w-full px-3 py-1 rounded border"
                                  />
                                </div>
                                <button
                                  onClick={() => removeSectionItem(section.id, itemIndex)}
                                  className="text-red-500 hover:text-red-700 self-end p-1"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                              <div>
                                <label className="block text-xs font-bold mb-1">Description</label>
                                <textarea
                                  value={item.description || ''}
                                  onChange={(e) => updateSectionItem(section.id, itemIndex, 'description', e.target.value)}
                                  className="w-full px-3 py-1 rounded border text-sm"
                                  rows="2"
                                />
                              </div>
                            </div>
                          ))}
                          {(!section.items || section.items.length === 0) && (
                            <p className="text-gray-500 text-sm text-center py-4">No items yet. Click "Add Item" to add content.</p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Gallery Items - Images and Videos */}
                    {section.type === 'gallery' && (
                      <div>
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-3">
                          <label className="block text-sm font-bold">Gallery Media (Images & Videos)</label>
                          <div className="flex gap-2">
                            <Button
                              onClick={() => addSectionItem(section.id, 'gallery', 'image')}
                              size="sm"
                              className="bg-purple-500 hover:bg-purple-600 gap-1"
                            >
                              <Image className="w-4 h-4" />
                              Add Image
                            </Button>
                            <Button
                              onClick={() => addSectionItem(section.id, 'gallery', 'video')}
                              size="sm"
                              className="bg-red-500 hover:bg-red-600 gap-1"
                            >
                              <Youtube className="w-4 h-4" />
                              Add Video
                            </Button>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          {section.items?.map((item, itemIndex) => (
                            <div key={itemIndex} className="relative border-2 rounded-lg overflow-hidden bg-gray-50">
                              {/* Video Item */}
                              {item.type === 'video' || item.youtube_url ? (
                                <div className="p-3">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Youtube className="w-5 h-5 text-red-500" />
                                    <span className="text-sm font-bold text-red-600">YouTube Video</span>
                                    <button
                                      onClick={() => removeSectionItem(section.id, itemIndex)}
                                      className="ml-auto p-1 text-red-500 hover:bg-red-50 rounded"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                  <input
                                    type="url"
                                    value={item.youtube_url || ''}
                                    onChange={(e) => updateSectionItem(section.id, itemIndex, 'youtube_url', e.target.value)}
                                    placeholder="https://youtube.com/watch?v=..."
                                    className="w-full px-3 py-2 rounded border-2 focus:border-red-500 focus:outline-none text-sm"
                                  />
                                  {getYouTubeVideoId(item.youtube_url) && (
                                    <div className="mt-2 aspect-video rounded overflow-hidden">
                                      <img 
                                        src={`https://img.youtube.com/vi/${getYouTubeVideoId(item.youtube_url)}/mqdefault.jpg`}
                                        alt="Video thumbnail"
                                        className="w-full h-full object-cover"
                                      />
                                    </div>
                                  )}
                                  <input
                                    type="text"
                                    value={item.caption || ''}
                                    onChange={(e) => updateSectionItem(section.id, itemIndex, 'caption', e.target.value)}
                                    placeholder="Video caption (optional)"
                                    className="w-full px-3 py-2 mt-2 rounded border focus:border-red-500 focus:outline-none text-sm"
                                  />
                                </div>
                              ) : (
                                /* Image Item */
                                <div className="p-3">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Image className="w-5 h-5 text-purple-500" />
                                    <span className="text-sm font-bold text-purple-600">Image / Video</span>
                                    <button
                                      onClick={() => removeSectionItem(section.id, itemIndex)}
                                      className="ml-auto p-1 text-red-500 hover:bg-red-50 rounded"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                  {item.url ? (
                                    <div className="relative aspect-video rounded overflow-hidden border mb-2">
                                      {item.is_video || /\.(mp4|mov|webm)$/i.test(item.url || '') ? (
                                        <video src={item.url} className="w-full h-full object-cover" muted playsInline controls />
                                      ) : (
                                        <img src={item.url} alt="" className="w-full h-full object-cover" />
                                      )}
                                      <button
                                        type="button"
                                        onClick={() => updateSectionItem(section.id, itemIndex, 'url', '')}
                                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                                      >
                                        <X className="w-4 h-4" />
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="flex flex-col items-center justify-center aspect-video rounded-lg border-2 border-dashed border-gray-300 mb-2 p-4">
                                      <Upload className="w-8 h-8 text-gray-400 mb-2" />
                                      <div className="flex gap-2">
                                        <label className="cursor-pointer px-3 py-2 bg-purple-500 hover:bg-purple-600 text-white text-xs rounded font-medium">
                                          Upload
                                          <input
                                            type="file"
                                            accept="image/*,video/*"
                                            onChange={(e) => handleImageUpload(section.id, itemIndex, e)}
                                            className="hidden"
                                          />
                                        </label>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setImagePickerTarget({ sectionId: section.id, itemIndex, field: 'url' });
                                            setShowImagePicker(true);
                                          }}
                                          className="px-3 py-2 bg-pink-500 hover:bg-pink-600 text-white text-xs rounded font-medium flex items-center gap-1"
                                        >
                                          <Image className="w-3 h-3" />
                                          Gallery
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                  <input
                                    type="text"
                                    value={item.url || ''}
                                    onChange={(e) => {
                                      updateSectionItem(section.id, itemIndex, 'url', e.target.value);
                                      updateSectionItem(section.id, itemIndex, 'type', 'image');
                                    }}
                                    placeholder="Or paste image URL"
                                    className="w-full px-3 py-2 rounded border focus:border-purple-500 focus:outline-none text-sm"
                                  />
                                  <input
                                    type="text"
                                    value={item.caption || ''}
                                    onChange={(e) => updateSectionItem(section.id, itemIndex, 'caption', e.target.value)}
                                    placeholder="Image caption (optional)"
                                    className="w-full px-3 py-2 mt-2 rounded border focus:border-purple-500 focus:outline-none text-sm"
                                  />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        {(!section.items || section.items.length === 0) && (
                          <p className="text-gray-500 text-sm text-center py-4">No media yet. Click "Add Image" or "Add Video" to start.</p>
                        )}
                      </div>
                    )}

                    {/* Video Section - Single Featured Video */}
                    {section.type === 'video' && (
                      <div>
                        <div className="p-4 bg-red-50 rounded-lg border-2 border-red-200">
                          <div className="flex items-center gap-2 mb-3">
                            <Youtube className="w-6 h-6 text-red-500" />
                            <span className="font-bold text-red-700">Featured YouTube Video</span>
                          </div>
                          <input
                            type="url"
                            value={section.youtube_url || ''}
                            onChange={(e) => updateSection(section.id, 'youtube_url', e.target.value)}
                            placeholder="https://youtube.com/watch?v=... or https://youtu.be/..."
                            className="w-full px-4 py-2 rounded-lg border-2 focus:border-red-500 focus:outline-none"
                          />
                          {getYouTubeVideoId(section.youtube_url) && (
                            <div className="mt-3 aspect-video rounded-lg overflow-hidden max-w-xl">
                              <img 
                                src={`https://img.youtube.com/vi/${getYouTubeVideoId(section.youtube_url)}/maxresdefault.jpg`}
                                alt="Video thumbnail"
                                className="w-full h-full object-cover"
                              />
                              <p className="text-sm text-gray-600 mt-2">✓ Video ID: {getYouTubeVideoId(section.youtube_url)}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {(!pageContent.sections || pageContent.sections.length === 0) && (
              <div className="text-center py-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                <p className="text-gray-500 mb-4">No sections yet. Add your first section!</p>
                <Button
                  onClick={() => {
                    setNewSection({ type: 'hero', title: '', position: 0 });
                    setShowAddSection(true);
                  }}
                  className="bg-green-500 hover:bg-green-600 gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add First Section
                </Button>
              </div>
            )}
          </div>

          {/* Save Button - Centered at bottom */}
          <div className="flex flex-col sm:flex-row justify-center gap-3 pt-6 border-t">
            <Button
              onClick={() => window.open(`/${pageContent.page_id === 'home' ? '' : pageContent.page_id}`, '_blank')}
              variant="outline"
              className="gap-2"
            >
              <Eye className="w-4 h-4" />
              Preview Page
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-purple-500 hover:bg-purple-600 gap-2 px-8"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      )}

      {!selectedPage && (
        <div className="text-center py-12 text-gray-500">
          <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-lg">Select a page above to edit its content</p>
        </div>
      )}

      {/* Image Picker Modal */}
      <ImagePickerModal
        open={showImagePicker}
        onClose={() => {
          setShowImagePicker(false);
          setImagePickerTarget(null);
        }}
        onSelect={(url) => {
          if (imagePickerTarget) {
            const { sectionId, itemIndex, field } = imagePickerTarget;
            updateSectionItem(sectionId, itemIndex, field, url);
            updateSectionItem(sectionId, itemIndex, 'type', 'image');
          }
          setShowImagePicker(false);
          setImagePickerTarget(null);
        }}
        currentImage={
          imagePickerTarget 
            ? pageContent?.sections?.find(s => s.id === imagePickerTarget.sectionId)?.items?.[imagePickerTarget.itemIndex]?.url || ''
            : ''
        }
      />
    </div>
  );
};

export default PagesAdmin;
